import React, { useState } from 'react';
import { AlertTriangle, Clock, User, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../components/ui/Button';

const EscalatedAlertCard = ({ alert, onAction }) => {
  const [expanded, setExpanded] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEscalationBadge = (level) => {
    switch (level) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-300';
      case 'overdue': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'escalated': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-4">
        {/* Alert Icon */}
        <div className={`p-2 rounded-lg ${getSeverityColor(alert?.severity)}`}>
          <AlertTriangle className="w-5 h-5" />
        </div>

        {/* Alert Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">{alert?.title}</h3>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getSeverityColor(alert?.severity)}`}>
                  {alert?.severity?.toUpperCase()}
                </span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getEscalationBadge(alert?.escalationLevel)}`}>
                  {alert?.escalationLevel?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{alert?.message}</p>
            </div>

            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              {expanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
            </button>
          </div>

          {/* Alert Metadata */}
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatTimeAgo(alert?.createdAt)}</span>
            </div>
            {alert?.createdByProfile && (
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{alert?.createdByProfile?.full_name}</span>
              </div>
            )}
            {alert?.user && (
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{alert?.user?.full_name}</span>
              </div>
            )}
            {alert?.responseTime && (
              <span className="text-green-600 font-medium">
                Responded in {alert?.responseTime}m
              </span>
            )}
          </div>

          {/* Expanded Details */}
          {expanded && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Alert Type:</p>
                  <p className="font-medium text-gray-900">{alert?.type}</p>
                </div>
                <div>
                  <p className="text-gray-600">Created:</p>
                  <p className="font-medium text-gray-900">{new Date(alert?.createdAt)?.toLocaleString()}</p>
                </div>
                {alert?.dueDate && (
                  <div>
                    <p className="text-gray-600">Due Date:</p>
                    <p className="font-medium text-gray-900">{new Date(alert?.dueDate)?.toLocaleString()}</p>
                  </div>
                )}
                {alert?.location && (
                  <div>
                    <p className="text-gray-600">Location:</p>
                    <p className="font-medium text-gray-900">{alert?.location}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  onClick={() => onAction(alert, 'reassign')}
                  className="flex items-center gap-1"
                >
                  Reassign
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAction(alert, 'escalate')}
                  className="flex items-center gap-1"
                >
                  Escalate to Management
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAction(alert, 'disciplinary')}
                  className="flex items-center gap-1 text-red-600 hover:text-red-700"
                >
                  Disciplinary Action
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalatedAlertCard;